import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../api/axios";

export default function SessionAttendance({ session, onCancelStart, onCancelled, disabled = false }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchSequence = useRef(0);
  useEffect(() => () => { searchSequence.current += 1; }, []);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cancellingRef = useRef(false);
  const base = `/api/checkin/sessions/${session.id}`;

  async function search(e) {
    e.preventDefault();
    const sequence = ++searchSequence.current;
    setSearching(true); setMatches(null); setError(""); setNotice("");
    try {
      const { data } = await axiosInstance.get(`${base}/records`, { params: { q: query.trim(), per_page: 20 } });
      if (sequence === searchSequence.current) setMatches(data);
    } catch (e) {
      if (sequence === searchSequence.current) setError(e.response?.data?.message || "Unable to find check-ins. Please try again.");
    } finally { if (sequence === searchSequence.current) setSearching(false); }
  }
  async function cancel(record) {
    if (cancellingRef.current || disabled) return;
    cancellingRef.current = true;
    setCancelling(true); setError(""); setNotice("");
    onCancelStart();
    try {
      await axiosInstance.post(`${base}/records/${record.id}/cancel`, { checked_in_at: record.checked_in_at });
      setNotice(`Check-in cancelled for ${record.first} ${record.last} (${record.email}). They can check in again while the session is open.`);
      setMatches(previous => previous ? { ...previous, total: Math.max(0, previous.total - 1), records: previous.records.filter(item => item.id !== record.id) } : null);
      await onCancelled();
    } catch (e) { setError(e.response?.data?.message || "Unable to cancel check-in. Search again and retry."); }
    finally { cancellingRef.current = false; setCancelling(false); }
  }
  async function exportCsv() {
    setExporting(true); setError("");
    try {
      const response = await axiosInstance.get(`${base}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `checkins-${session.name.replace(/[^a-zA-Z0-9_-]/g, "_")}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError("Unable to export check-ins. Please try again."); }
    finally { setExporting(false); }
  }

  return <section className="checkin-panel" aria-labelledby="attendance-title">
    <div className="checkin-attendance-heading">
      <div><h2 id="attendance-title">Manage check-ins</h2><p>{session.name} · {session.count} checked in</p></div>
      <button type="button" disabled={exporting} onClick={exportCsv}>{exporting ? "Exporting…" : "Export check-ins (CSV)"}</button>
    </div>
    <p>Find a hacker by name or email, then cancel their check-in for this session.</p>
    <form onSubmit={search}>
      <label htmlFor="cancel-checkin-query">Name or email to cancel</label>
      <input id="cancel-checkin-query" required minLength={2} maxLength={100} autoComplete="off" placeholder="Name or email" value={query} disabled={cancelling} onChange={e => {
        setQuery(e.target.value); searchSequence.current += 1;
        setSearching(false); setMatches(null); setError(""); setNotice("");
      }} />
      <button type="submit" disabled={searching || cancelling || query.trim().length < 2}>{searching ? "Searching…" : "Find check-in"}</button>
    </form>
    {matches && <>
      {matches.total === 0 && <p>No active check-ins found for this search in {session.name}.</p>}
      <ul className="checkin-hackers">{matches.records.map(record => <li key={record.id}>
        <strong>{record.first} {record.last}</strong><span>{record.email}</span>
        <span>{new Date(record.checked_in_at).toLocaleString()}</span>
        <button type="button" className="checkin-cancel" disabled={disabled || cancelling} aria-label={`Cancel check-in for ${record.email}`} onClick={() => cancel(record)}>Cancel check-in</button>
      </li>)}</ul>
      {matches.total > matches.records.length && <p>Showing {matches.records.length} of {matches.total} matches. Refine the name or email to find the correct hacker.</p>}
    </>}
    {error && <p role="alert" className="checkin-feedback checkin-feedback--error">{error}</p>}
    {notice && <p role="status">{notice}</p>}
  </section>;
}
